import IconCancelOutlined from "@mui/icons-material/esm/CancelOutlined";
import type {
  Breakpoint,
  CssVarsThemeOptions,
  PaletteColor,
  PaletteColorChannel,
  PaletteColorOptions,
} from "@mui/material/styles";
import type {} from "@mui/material/themeCssVarsAugmentation";
import type { CSSProperties } from "react";

/** Aura extensions for the Material UI theme palette options. */
type AuraPaletteOptionsExtensions = {
  fountain: {
    primary: {
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      850: string;
      900: string;
      950: string;
    };
    secondary: {
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      850: string;
      900: string;
    };
  };
  neutral: PaletteColorOptions & Partial<PaletteColorChannel>;
  other: {
    divider: string;
  };
  tertiary: PaletteColorOptions & Partial<PaletteColorChannel>;
  tooltip: {
    error: {
      bg: string;
      text: string;
    };
  };
};

/** Aura extensions for the Material UI theme palette. */
type AuraPaletteExtensions = Pick<
  AuraPaletteOptionsExtensions,
  "fountain" | "other" | "tooltip"
> & {
  neutral: PaletteColor & PaletteColorChannel;
  tertiary: PaletteColor & PaletteColorChannel;
};

/**
 * Aura extensions for the Material UI theme typography variants options and
 * resulting theme typography variants.
 */
type AuraTypographyVariantsExtensions = {
  body3: CSSProperties;
  subtitle3: CSSProperties;
  tooltip: CSSProperties;
};

/** Aura extensions for the Material UI theme options and resulting theme. */
type AuraThemeExtensions = {
  constants: {
    appBar: {
      height: {
        [key in Breakpoint]: number;
      };
    };
    breadcrumbs: {
      breadcrumbsHeight: number;
      height: {
        [key in Breakpoint]: number;
      };
    };
    defaultContentLayout?: {
      getMX: () => number;
      getMY: () => number;
      getPX: () => number;
      getPY: () => number;
      spacing: {
        marginBottom: number;
        marginLeft: number;
        marginRight: number;
        marginTop: number;
        paddingBottom: number;
        paddingLeft: number;
        paddingRight: number;
        paddingTop: number;
      };
    };
  };
};

// Augment the Material UI theme types with Aura extensions to prevent type
// errors when the Aura theme is created or used in components. Augmenting
// module type definitions like this is an anti-pattern (e.g. it prevents a
// project from using multiple themes with correct types), but it’s sadly the
// way the Material UI theme is designed to be extended:
// - https://mui.com/material-ui/experimental-api/css-theme-variables/customization/#typescript
// - https://mui.com/material-ui/customization/typography/#adding-amp-disabling-variants
declare module "@mui/material/styles" {
  /*
    eslint-disable
    @typescript-eslint/consistent-type-definitions,
    @typescript-eslint/no-empty-interface
  */
  interface PaletteOptions extends AuraPaletteOptionsExtensions {}
  interface Palette extends AuraPaletteExtensions {}
  interface TypographyVariantsOptions
    extends AuraTypographyVariantsExtensions {}
  interface TypographyVariants extends AuraTypographyVariantsExtensions {}
  interface ThemeOptions extends AuraThemeExtensions {}
  interface Theme extends AuraThemeExtensions {}
  /*
    eslint-enable
    @typescript-eslint/consistent-type-definitions,
    @typescript-eslint/no-empty-interface
  */
}

declare module "@mui/material/Button" {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface ButtonPropsColorOverrides {
    neutral: true;
  }
}

// CSS color values used in the theme. They are defined as constants here and
// then referenced throughout the theme to make it easy to track all the colors
// used, and to improve bundle size by avoiding duplicate color values.
const COLOR_000000 = "#000";
const COLOR_00000A = "#00000A";
const COLOR_00001F = "#00001F";
const COLOR_202020 = "#202020";
const COLOR_212121 = "#212121";
const COLOR_393939 = "#393939";
const COLOR_424242 = "#424242";
const COLOR_616161 = "#616161";
const COLOR_757575 = "#757575";
const COLOR_9E9E9E = "#9E9E9E";
const COLOR_BDBDBD = "#BDBDBD";
const COLOR_E0E0E0 = "#E0E0E0";
const COLOR_EEEEEE = "#EEEEEE";
const COLOR_F5F5F5 = "#F5F5F5";
const COLOR_FAFAFA = "#FAFAFA";
const COLOR_FFFFFF = "#FFF";
const COLOR_064CA3 = "#064CA3";
const COLOR_004C97 = "#004C97";
const COLOR_0750AA = "#0750AA";
const COLOR_0752B0 = "#0752B0";
const COLOR_0758BC = "#0758BC";
const COLOR_0863D4 = "#0863D4";
const COLOR_096FED = "#096FED";
const COLOR_378DF7 = "#378DF7";
const COLOR_6DACF9 = "#6DACF9";
const COLOR_A3CAFB = "#A3CAFB";
const COLOR_D9E9FE = "#D9E9FE";
const COLOR_F4F9FF = "#F4F9FF";
const COLOR_4a148c = "#4a148c";
const COLOR_9c27b0 = "#9c27b0";
const COLOR_f3e5f5 = "#f3e5f5";
const COLOR_541313 = "#541313";
const COLOR_C62828 = "#C62828";
const COLOR_D32F2F = "#D32F2F";
const COLOR_EF5350 = "#EF5350";
const COLOR_FBEAEA = "#FBEAEA";
const COLOR_D04900 = "#D04900";
const COLOR_C15700 = "#C15700";
const COLOR_FF9800 = "#FF9800";
const COLOR_1B5E20 = "#1B5E20";
const COLOR_2E7D32 = "#2E7D32";
const COLOR_4CAF50 = "#4CAF50";

/**
 * Aura Material UI theme options object.
 *
 * It uses the Material UI experimental
 * [CSS theme variables](https://mui.com/material-ui/experimental-api/css-theme-variables)
 * system, which requires using from `@mui/material/styles` the function
 * `extendTheme` to create the theme, and the React component `CssVarsProvider`
 * to provide the theme for the entire app.
 * @example
 * How to setup a themed app:
 *
 * ```tsx
 * import ScopedCssBaseline from "@mui/material/ScopedCssBaseline";
 * import {
 *   Experimental_CssVarsProvider as CssVarsProvider,
 *   experimental_extendTheme as extendTheme,
 * } from "@mui/material/styles";
 * import themeOptions from "@tricentis/aura/constants/themeOptions.js";
 *
 * const theme = extendTheme(themeOptions);
 * const app = (
 *   <CssVarsProvider theme={theme}>
 *     <ScopedCssBaseline>Your app…</ScopedCssBaseline>
 *   </CssVarsProvider>
 * );
```
 */
const themeOptions = {
  // Custom.
  constants: {
    appBar: {
      height: {
        xs: 48,
        sm: 48,
        md: 48,
        lg: 48,
        xl: 48,
      },
    },
    breadcrumbs: {
      breadcrumbsHeight: 40,
      height: {
        xs: 40,
        sm: 40,
        md: 40,
        lg: 40,
        xl: 40,
      },
    },
    defaultContentLayout: {
      spacing: {
        marginBottom: 3,
        marginLeft: 3,
        marginRight: 3,
        marginTop: 3,
        paddingBottom: 3,
        paddingLeft: 3,
        paddingRight: 3,
        paddingTop: 3,
      },
      getMX() {
        return this.spacing.marginTop + this.spacing.marginBottom;
      },
      getPX() {
        return this.spacing.paddingTop + this.spacing.paddingBottom;
      },
      getMY() {
        return this.spacing.marginRight + this.spacing.marginLeft;
      },
      getPY() {
        return this.spacing.paddingRight + this.spacing.paddingLeft;
      },
    },
  },
  colorSchemes: {
    light: {
      palette: {
        primary: {
          light: COLOR_6DACF9,
          main: COLOR_096FED,
          dark: COLOR_064CA3,
          contrastText: COLOR_FFFFFF,
        },
        secondary: {
          light: COLOR_616161,
          main: COLOR_212121,
          dark: COLOR_000000,
          contrastText: COLOR_FFFFFF,
        },
        // Custom.
        tertiary: {
          light: COLOR_f3e5f5,
          main: COLOR_9c27b0,
          dark: COLOR_4a148c,
          contrastText: COLOR_FFFFFF,
        },
        // Custom.
        neutral: {
          light: COLOR_00000A,
          main: COLOR_00000A,
          dark: COLOR_00001F,
          contrastText: COLOR_202020,
        },
        error: {
          light: COLOR_EF5350,
          main: COLOR_D32F2F,
          dark: COLOR_C62828,
          contrastText: COLOR_FFFFFF,
        },
        warning: {
          light: COLOR_FF9800,
          main: COLOR_C15700,
          dark: COLOR_D04900,
          contrastText: COLOR_FFFFFF,
        },
        info: {
          light: COLOR_6DACF9,
          main: COLOR_096FED,
          dark: COLOR_064CA3,
          contrastText: COLOR_FFFFFF,
        },
        success: {
          light: COLOR_4CAF50,
          main: COLOR_2E7D32,
          dark: COLOR_1B5E20,
          contrastText: COLOR_FFFFFF,
        },
        text: {
          primary: "rgba(0, 0, 0, 0.87)",
          secondary: "rgba(0, 0, 0, 0.54)",
          disabled: "rgba(0, 0, 0, 0.38)",
        },
        action: {
          active: "rgba(0, 0, 0, 0.54)",
          hover: "rgba(0, 0, 0, 0.03)",
          selected: "rgba(0, 0, 0, 0.08)",
          selectedOpacity: 0.08,
          disabled: "rgba(0, 0, 0, 0.26)",
          disabledBackground: "rgba(0, 0, 0, 0.12)",
          disabledOpacity: 0.38,
          focus: "rgba(0, 0, 0, 0.12)",
        },
        // Custom.
        other: {
          divider: "rgba(33, 33, 33, 0.12)",
        },
        // Custom.
        tooltip: {
          error: {
            bg: COLOR_FBEAEA,
            text: COLOR_541313,
          },
        },
        // Custom.
        fountain: {
          primary: {
            50: COLOR_F4F9FF,
            100: COLOR_D9E9FE,
            200: COLOR_A3CAFB,
            300: COLOR_6DACF9,
            400: COLOR_378DF7,
            500: COLOR_096FED,
            600: COLOR_0863D4,
            700: COLOR_0758BC,
            800: COLOR_0752B0,
            850: COLOR_0750AA,
            900: COLOR_064CA3,
            950: COLOR_004C97,
          },
          secondary: {
            50: COLOR_FAFAFA,
            100: COLOR_F5F5F5,
            200: COLOR_EEEEEE,
            300: COLOR_E0E0E0,
            400: COLOR_BDBDBD,
            500: COLOR_9E9E9E,
            600: COLOR_757575,
            700: COLOR_616161,
            800: COLOR_424242,
            850: COLOR_393939,
            900: COLOR_212121,
          },
        },
      },
    },
  },
  typography: {
    fontFamily: "Inter",
    h1: {
      fontWeight: 400,
      fontSize: "4.00rem",
      lineHeight: "5.25rem",
      letterSpacing: "0",
    },
    h2: {
      fontWeight: 400,
      fontSize: "3.25rem",
      lineHeight: "4.25rem",
      letterSpacing: "0",
    },
    h3: {
      fontWeight: 400,
      fontSize: "2.50rem",
      lineHeight: "3.25rem",
      letterSpacing: "0",
    },
    h4: {
      fontWeight: 400,
      fontSize: "2rem",
      lineHeight: "2.5rem",
      letterSpacing: "0",
    },
    h5: {
      fontWeight: 400,
      fontSize: "1.75rem",
      lineHeight: "2.25rem",
      letterSpacing: "0",
    },
    h6: {
      fontWeight: 600,
      fontSize: "1.375rem",
      lineHeight: "1.75rem",
      letterSpacing: "0",
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: "1rem",
      lineHeight: "1.5rem",
      letterSpacing: "0",
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: "0.875rem",
      lineHeight: "1.125rem",
      letterSpacing: "0.007em",
    },
    // Custom.
    subtitle3: {
      fontWeight: 500,
      fontSize: "0.75rem",
      lineHeight: "1rem",
      letterSpacing: "0.015em",
      fontFamily: "Inter",
    },
    body1: {
      fontWeight: 400,
      fontSize: "1rem",
      lineHeight: "1.5rem",
      letterSpacing: "0",
    },
    body2: {
      fontWeight: 400,
      fontSize: "0.875rem",
      lineHeight: "1.25rem",
      letterSpacing: "0.007em",
    },
    // Custom.
    body3: {
      fontWeight: 400,
      fontSize: "0.75rem",
      lineHeight: "1rem",
      letterSpacing: "0.015em",
      fontFamily: "Inter",
    },
    caption: {
      fontWeight: 400,
      fontSize: "0.75rem",
      lineHeight: "1rem",
      letterSpacing: "0.015em",
    },
    overline: {
      fontWeight: 400,
      fontSize: "0.625rem",
      lineHeight: "0.875rem",
      letterSpacing: "0.015em",
    },
    // Custom.
    tooltip: {
      fontWeight: 500,
      fontSize: "0.75rem",
      lineHeight: "1rem",
      letterSpacing: 0,
    },
    button: {
      textTransform: "none",
    },
  },
  components: {
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState, theme }) =>
          ownerState.variant !== "filled"
            ? {
                border: `1px solid ${
                  theme.vars.palette[
                    ownerState.color ?? ownerState.severity ?? "success"
                  ].main
                }`,
              }
            : {},
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.vars.palette.grey[50],
          borderBottom: `1px solid ${theme.vars.palette.other.divider}`,
          boxShadow: "none",
          color: theme.vars.palette.neutral.contrastText,
          gap: theme.spacing(2),
          height: theme.constants.appBar.height.xs,
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
          "& > .MuiToolbar-root": {
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            width: "100%",
            [theme.breakpoints.up("xs")]: {
              height: theme.constants.appBar.height.xs,
              minHeight: theme.constants.appBar.height.xs,
              paddingLeft: 0,
              paddingRight: 0,
            },
            "& > .MuiBox-root": {
              alignItems: "center",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "row",
              gap: theme.spacing(2),
              height: "100%",
            },
          },
        }),
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.spacing(0.75),
          "&.MuiButton-containedNeutral": {
            backgroundColor: `rgba(${theme.vars.palette.secondary.darkChannel} / 0.04)`,
            "&:hover": {
              backgroundColor: `rgba(${theme.vars.palette.secondary.darkChannel} / 0.12)`,
            },
          },
          "&.MuiButton-outlinedNeutral": {
            color: theme.vars.palette.neutral.contrastText,
            borderColor: theme.vars.palette.neutral.contrastText,
          },
          "&.MuiButton-textNeutral": {
            color: theme.vars.palette.neutral.contrastText,
          },
        }),
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: ({ theme }) => ({
          display: "flex",
          alignItems: "center",
          marginInline: theme.spacing(2.5),
          borderBottom: `1px solid ${theme.vars.palette.divider}`,
          paddingBlock: theme.spacing(0.5),
          ...theme.typography.body3,
        }),
        li: ({ theme }) => ({
          display: "flex",
          alignItems: "center",
          "&:last-child": {
            color: theme.vars.palette.text.primary,
          },
        }),
      },
    },
    MuiChip: {
      defaultProps: {
        deleteIcon: <IconCancelOutlined />,
      },
      styleOverrides: {
        filledPrimary: ({ ownerState: { clickable }, theme }) => ({
          color: theme.vars.palette.text.primary,
          backgroundColor: `rgba(${theme.vars.palette.primary.mainChannel} / ${theme.vars.palette.action.selectedOpacity})`,
          ...(clickable && {
            "&:hover": {
              backgroundColor: `rgba(${theme.vars.palette.primary.mainChannel} / 0.2)`,
            },
          }),
        }),
        deleteIcon: {
          color: "inherit",
          opacity: 0.7,
          "&:hover": {
            color: "inherit",
            opacity: 1,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        vertical: {
          borderRightWidth: "2px",
          "&::before, &::after": {
            borderLeftWidth: "2px",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        sizeSmall: {
          padding: 4,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&.Mui-selected, &:hover": {
            color: theme.vars.palette.primary.main,
          },
        }),
      },
    },
    MuiSnackbar: {
      defaultProps: {
        autoHideDuration: 10_000,
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          ...theme.typography.tooltip,
        }),
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: ({ theme }) => ({
          ".Mui-disabled &": {
            color: theme.vars.palette.text.disabled,
          },
        }),
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: `${theme.spacing(3)} ${theme.spacing(2.5)}`,
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(3),
          color: theme.vars.palette.text.secondary,
        }),
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(3),
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: `${theme.spacing(4)} ${theme.spacing(4)} ${theme.spacing(
            1,
          )}`,
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: `${theme.spacing(1)} ${theme.spacing(4)}`,
          color: theme.vars.palette.text.primary,
        }),
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.vars.palette.text.primary,
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: `${theme.spacing(1)} ${theme.spacing(4)} ${theme.spacing(
            4,
          )}`,
        }),
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: ({ theme }) => ({
          flexDirection: "row-reverse",
          gap: theme.spacing(1),
          transition: "all 150ms linear",
        }),
        expanded: ({ theme }) => ({
          minHeight: "auto",
          marginBottom: theme.spacing(1),
          "&.MuiAccordionSummary-content": {
            marginBlock: theme.spacing(1.5),
          },
        }),
        disabled: ({ theme }) => ({
          opacity: 1,
          "& .MuiTypography-root": {
            color: theme.vars.palette.text.disabled,
          },
          "& .MuiAccordionSummary-expandIconWrapper": {
            color: theme.vars.palette.text.disabled,
          },
        }),
        expandIconWrapper: {
          transform: "rotate(-90deg)",
          "&.Mui-expanded": {
            transform: "rotate(0deg)",
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 40,
          padding: theme.spacing(1),
          "&:hover": {
            backgroundColor: theme.vars.palette.action.hover,
          },
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
        },
      },
    },
  },
} as const satisfies CssVarsThemeOptions;

export default themeOptions;
